import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { DndContext } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { difference, get, isEmpty } from 'lodash';
import { fieldMappingSelector } from 'slices/field.slice';
import { selectObjectTypesMap } from 'slices/objectTypes.slice';
import { setFieldsSetting } from 'slices/reportTemplate.slice';
import { useAppDispatch } from 'slices/store';
import type { TReportTemplate } from '@timeedit/types';
import { Icon, TEDrawerSection, TEInput } from '@timeedit/ui-components';
import { Button, Table } from '@timeedit/ui-components/antd';
import type { ColumnsType } from '@timeedit/ui-components/antd/table/index';
import type { TReportTemplateProps } from './ReportTemplate';

const FieldSettings = ({ data, onChange }: TReportTemplateProps) => {
  const dispatch = useAppDispatch();

  const [localFieldSettings, setLocalFieldSettings] = useState<
    TReportTemplate['fieldSettings']
  >([]);

  const objectTypesMap = useSelector(selectObjectTypesMap);
  const fieldMapping = useSelector(fieldMappingSelector);

  const fieldSettings = data?.fieldSettings || [];

  const objectTypeFieldIds = useMemo(() => {
    if (
      isEmpty(fieldMapping) ||
      isEmpty(fieldMapping) ||
      !data?.targetObjectType
    ) {
      return [];
    }

    const objectType = objectTypesMap[data.targetObjectType];
    const ownFields = objectType?.fields ?? [];
    const parentFields = objectType?.parentFields ?? [];

    return parentFields.concat(ownFields);
  }, [data?.targetObjectType, fieldMapping, objectTypesMap]);

  useEffect(() => {
    if (isEmpty(objectTypeFieldIds)) {
      return;
    }

    const unsetFieldIds = difference(
      objectTypeFieldIds,
      fieldSettings.map((i) => i.id)
    );

    const newFieldSettings = unsetFieldIds.map((id) => ({
      id,
      display: true,
      filter: true,
    }));

    setLocalFieldSettings([...fieldSettings, ...newFieldSettings]);
  }, [objectTypeFieldIds, fieldSettings]);

  useEffect(() => {
    // Init field settings if not exist
    if (!isEmpty(objectTypeFieldIds) && data && isEmpty(data.fieldSettings)) {
      const fieldsSettingsInit = objectTypeFieldIds.map((id) => ({
        id,
        display: true,
        filter: true,
      }));

      onChange('fieldSettings', fieldsSettingsInit);
      dispatch(
        setFieldsSetting({ id: data.id, fieldSettings: fieldsSettingsInit })
      );
    }
  }, [data, onChange, objectTypeFieldIds, dispatch]);

  const onChangeFieldSetting = useCallback(
    (id: string | number, type: string, val: boolean) => {
      if (data?.fieldSettings) {
        setLocalFieldSettings((oldLocalFieldSettings) => {
          const newFieldSettings = oldLocalFieldSettings.map((field) => {
            if (id === field.id) {
              return {
                ...field,
                [type]: val,
              };
            }
            return field;
          });

          onChange('fieldSettings', newFieldSettings);
          return newFieldSettings;
        });
      }
    },
    [data?.fieldSettings, onChange]
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setLocalFieldSettings((prevState) => {
        const activeIndex = prevState.findIndex(
          (record) => record.id === active?.id
        );
        const overIndex = prevState.findIndex(
          (record) => record.id === over?.id
        );
        const newList = arrayMove(prevState, activeIndex, overIndex);
        onChange('fieldSettings', newList);
        return newList;
      });
    }
  };

  const columns: ColumnsType = useMemo(() => {
    return [
      { key: 'sort', align: 'center', width: 80, render: () => <DragHandle /> },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        render: (text, record) => {
          const { name = '', extId = '' } = fieldMapping?.[record.id] || {};
          return `${name} (${extId})`;
        },
      },
      {
        title: 'Type',
        dataIndex: 'fieldType',
        key: 'fieldType',
        ellipsis: true,
        width: 120,
        render: (text, record) => {
          const { fieldType = '' } = fieldMapping?.[record.id] || {};
          return fieldType;
        },
      },
      {
        title: 'Default visibility',
        className: 'cell-text-center',
        children: [
          {
            title: 'Column',
            dataIndex: 'id',
            key: 'Column',
            width: 80,
            render: (id, obj) => {
              return (
                <TEInput.Switch
                  checked={get(obj, `display`, false)}
                  onChange={(val) => onChangeFieldSetting(id, 'display', val)}
                  size="small"
                />
              );
            },
          },
          {
            title: 'Filter',
            dataIndex: 'id',
            align: 'center',
            width: 80,
            render: (id, obj) => {
              return (
                <TEInput.Switch
                  size="small"
                  checked={get(obj, `filter`, false)}
                  onChange={(val) => onChangeFieldSetting(id, 'filter', val)}
                />
              );
            },
          },
        ],
      },
    ];
  }, [fieldMapping, onChangeFieldSetting]);

  return (
    <TEDrawerSection label="Fields settings">
      <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext
          items={localFieldSettings.map((i) => i.id)}
          strategy={verticalListSortingStrategy}
        >
          <Table
            rowKey="id"
            components={{ body: { row: Row } }}
            columns={columns}
            dataSource={localFieldSettings}
            pagination={false}
            scroll={{ y: 240 }}
            size="small"
          />
        </SortableContext>
      </DndContext>
    </TEDrawerSection>
  );
};

export default FieldSettings;

const DragHandle: React.FC = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<Icon id="HolderOutlined" alt="Holder" />}
      style={{ cursor: 'move' }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: SyntheticListenerMap;
}

const RowContext = React.createContext<RowContextProps>({});

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

const Row: React.FC<RowProps> = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props['data-row-key'] });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  const contextValue = useMemo<RowContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners]
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};
